import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { ref, onValue, off } from "firebase/database";
import { db } from "../../../configs/Firebase";
import Select from "../../../designSystem/b-molecules/formElements/select/Select";
import Fuse from "fuse.js";
import style from "./PersonaLinkSelector.module.css";

const PersonaLinkSelector = ({ selectedPersonaIds = [], onChange, multiselect = false, orgId }) => {
  const org = useSelector((state) => state.orgStatus.value);
  const [personas, setPersonas] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredPersonas, setFilteredPersonas] = useState([]);

  const effectiveOrgId = orgId || org?.payload;

  // Load personas
  useEffect(() => {
    if (effectiveOrgId) {
      const personasRef = ref(db, `userData/${effectiveOrgId}/personas`);
      
      const unsubscribe = onValue(personasRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const personasList = Object.keys(data)
            .map(id => ({
              id,
              ...data[id]
            }))
            .filter(p => !p.archived)
            .sort((a, b) => {
              const aName = a.name || "";
              const bName = b.name || "";
              return aName.localeCompare(bName);
            });
          setPersonas(personasList);
          setFilteredPersonas(personasList);
        } else {
          setPersonas([]);
          setFilteredPersonas([]);
        }
      });

      return () => {
        off(personasRef);
      };
    }
  }, [effectiveOrgId]);

  // Search personas
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredPersonas(personas);
      return;
    }

    const fuse = new Fuse(personas, {
      keys: ['name', 'tagline', 'demographics.segment', 'demographics.role'],
      threshold: 0.3
    });

    const results = fuse.search(searchQuery);
    setFilteredPersonas(results.map(result => result.item));
  }, [searchQuery, personas]);

  const options = [
    { value: "Select Persona...", selected: false },
    ...filteredPersonas.map(persona => ({
      value: persona.name,
      personaId: persona.id,
      selected: selectedPersonaIds.includes(persona.id)
    }))
  ];

  const selectedOptions = multiselect
    ? personas
        .filter(p => selectedPersonaIds.includes(p.id))
        .map(p => ({ value: p.name, selected: true }))
    : selectedPersonaIds.length > 0
      ? personas
          .filter(p => selectedPersonaIds.includes(p.id))
          .map(p => ({ value: p.name, selected: true }))
          .slice(0, 1)
      : [{ value: "Select Persona...", selected: false }];

  const handleChange = (e, index) => {
    if (index === 0) {
      // "Select Persona..." selected - clear selection
      if (multiselect) {
        onChange([]);
      } else {
        onChange(null);
      }
    } else {
      const selectedPersona = filteredPersonas[index - 1];
      if (multiselect) {
        const isSelected = selectedPersonaIds.includes(selectedPersona.id);
        if (isSelected) {
          onChange(selectedPersonaIds.filter(id => id !== selectedPersona.id));
        } else {
          onChange([...selectedPersonaIds, selectedPersona.id]);
        }
      } else {
        onChange(selectedPersona.id);
      }
    }
  };

  return (
    <div className={style.container}>
      <Select
        label="Link Persona"
        type="dropdown"
        options={options}
        selectedOptions={selectedOptions.length > 0 ? selectedOptions : [{ value: "Select Persona..." }]}
        onChange={handleChange}
        multiselect={multiselect}
      />
    </div>
  );
};

export default PersonaLinkSelector;

